import React, { useState, useEffect, useRef } from 'react';
import DateInput from '../../components/DateInput';
import api from '../../api/axios';
import ReceiptPanel from './ReceiptPanel';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const fmtDate = d => d ? format(new Date(d), 'dd/MM/yyyy') : '—';
const inp = (label, key, form, set, type='text', req=false, readOnly=false) => (
  <div className="form-group">
    <label>{label}{req&&<span className="req"> *</span>}</label>
    {type === 'date'
      ? <DateInput className="form-control" value={form[key]||''} onChange={e=>set(f=>({...f,[key]:e.target.value}))} readOnly={readOnly} />
      : <input className="form-control" type={type} step={type==='number'?'0.001':undefined} value={form[key]||''} onChange={e=>set(f=>({...f,[key]:e.target.value}))} readOnly={readOnly} />}
  </div>
);

// MUI-style multi-select bound to a fixed list of options. Used in BL Info to
// pick shipping lines limited to the order's shipping_lines pool.
function ShippingLinesPicker({ options, value, onChange, placeholder = '— Select —' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (name) => {
    if (selected.includes(name)) onChange(selected.filter(n=>n!==name));
    else onChange([...selected, name]);
  };
  return (
    <div ref={ref} style={{position:'relative'}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px',padding:'6px 28px 6px 8px',border:'1px solid var(--border,#cbd5e1)',borderRadius:'6px',minHeight:'38px',background:'#fff',cursor:'pointer',position:'relative'}}>
        {selected.length === 0 && <span style={{color:'#94a3b8',fontSize:'13px'}}>{placeholder}</span>}
        {selected.map(name => (
          <span key={name} style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'#e0f2fe',color:'#0369a1',padding:'2px 6px',borderRadius:'10px',fontSize:'11px',fontWeight:500}}>
            {name}
            <button type="button" onClick={e=>{ e.stopPropagation(); onChange(selected.filter(n=>n!==name)); }} style={{background:'transparent',border:0,color:'inherit',fontSize:'13px',cursor:'pointer',padding:0,lineHeight:1}}>×</button>
          </span>
        ))}
        <span style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',fontSize:'10px',color:'#64748b'}}>▼</span>
      </div>
      {open && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'#fff',border:'1px solid #cbd5e1',borderRadius:'6px',boxShadow:'0 8px 20px rgba(15,23,42,0.12)',zIndex:50,maxHeight:'240px',overflowY:'auto'}}>
          {options.length === 0 ? (
            <div style={{padding:'10px',fontSize:'12px',color:'#94a3b8',textAlign:'center'}}>No shipping lines on the order — pick them on the order first.</div>
          ) : options.map(name => {
            const checked = selected.includes(name);
            return (
              <div key={name} onClick={()=>toggle(name)} style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 10px',cursor:'pointer',background: checked ? '#f1f5f9' : '#fff',fontSize:'13px'}} onMouseEnter={e=>e.currentTarget.style.background='#e0f2fe'} onMouseLeave={e=>e.currentTarget.style.background = checked ? '#f1f5f9' : '#fff'}>
                <input type="checkbox" checked={checked} readOnly style={{margin:0}} />
                <span style={{flex:1}}>{name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Friendly labels for auto-generated remark text on BL/shipment changes.
const SHIPMENT_FIELD_LABELS = {
  bl_no: 'BL No', date_of_bl: 'BL Date', lot_no: 'Lot No',
  bill_weight_mt: 'Bill Weight (MT)', no_of_containers: 'No. of Containers',
  avg_loading: 'Avg Loading (MT/cont)', shipping_line: 'Shipping Line',
  shipping_lines: 'Shipping Lines', port_of_loading: 'Port of Loading',
  port_of_discharge: 'Port of Discharge', route: 'Route',
  cha_id: 'CHA Agent', cha_name: 'CHA Agent', date_mail_to_cha: 'Date Mail to CHA',
  eta_date_port: 'ETA at Port', arrival_date_port: 'Actual Arrival at Port',
  port_status: 'Port Status', courier_date_to_cha: 'Courier Date to CHA',
  doc_release_date: 'Doc Release Date (Bank)', detention_free_days: 'Free Time (Days)',
  margin_days: 'Margin Days', exchange_rate: 'Exchange Rate', usd_value: 'USD Value',
  bc_applied_date: 'LC Applied Date', payment_date: 'Payment Date',
  payment_status: 'Payment Status', amount_usd: 'Amount in Dollar (USD)',
  discount_usd: 'Discount in USD', final_amount_usd: 'Final Amount (USD)',
  rate_per_usd: 'Rate per Dollar (INR)', amount_inr: 'Amount in INR',
  bill_of_entry_date: 'Bill of Entry Date', bill_of_entry_no: 'Bill of Entry No', custom_duty_status: 'Custom Duty Status',
  custom_duty_payment_date: 'Custom Duty Payment Date', haulage_date: 'Haulage Date',
  cha_bill_no: 'CHA Bill No', cha_bill_date: 'CHA Bill Date',
  transport_builty_no: 'Builty No', transport_builty_date: 'Builty Date',
  rail_out_date: 'Rail Out Date', icd_arrival_date: 'ICD Arrival Date',
  icd_location: 'ICD Location', container_dates: 'Containers', payment_type: 'Payment Type', due_date: 'Due Date',
  transport_mode: 'Transport Mode', vehicle_train_no: 'Vehicle / Train No',
  transport_agency: 'Transport Agency', dispatch_date: 'Dispatch Date',
  expected_delivery_date: 'Expected Delivery Date',
};

const PORT_STATUSES = ['Under Sea','On Ship','At Port','Customs','Released'];

// All shipment date columns that the panel binds to <DateInput> —
// must be sliced to YYYY-MM-DD before going into the form, otherwise the
// browser treats the ISO timestamp as invalid and shows an empty input
// (which then makes it look like the saved date got wiped).
const DATE_FIELDS = ['date_of_bl','date_lc_made','due_date','date_mail_to_cha','eta_date_port','arrival_date_port','bc_applied_date','payment_date','doc_release_date','courier_date_to_cha','doc_receipt_date_cha','free_time_upto','rail_out_date','icd_arrival_date','dispatch_date','expected_delivery_date'];
const normalizeDates = (obj) => {
  const out = { ...obj };
  for (const f of DATE_FIELDS) {
    if (out[f] && typeof out[f] === 'string' && out[f].includes('T')) {
      out[f] = out[f].split('T')[0];
    }
  }
  return out;
};
const PAYMENT_STATUSES = ['Pending','Paid'];
const PAYMENT_TYPES = ['LC','SBLC','DP'];
const SHIPPING_HEADS = ['Inspection Fees','Weightment Charges','THC Charges','Ground Rent Charges','Haulage Charges','Others'];
const r2 = n => Math.round((Number(n) || 0) * 100) / 100;

// Valid Date = ICD Arrival Date + Margin Days − 1
// Used by Tracking step's auto-fill. Returns YYYY-MM-DD or '' if either
// input is missing / invalid.
const calcValidDate = (icdArrival, marginDays) => {
  if (!icdArrival || marginDays == null || marginDays === '') return '';
  const d = new Date(icdArrival);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + parseInt(marginDays || 0) - 1);
  return d.toISOString().split('T')[0];
};

export default function ShipmentPanel({ order, onRefresh, flagsFor, onFlagClick }) {
  const FlagBadge = ({ scope, id, field }) => {
    // Red change-flags hidden in the BL view. Remarks are still logged/tracked
    // in the background and remain visible in the Remarks tab.
    return null;
    // eslint-disable-next-line no-unreachable
    if (typeof flagsFor !== 'function') return null;
    const nums = flagsFor(scope, id, field);
    if (!nums || nums.length === 0) return null;
    return (
      <span style={{display:'inline-flex',gap:'2px',marginLeft:'6px'}}>
        {nums.map(n => (
          <button
            key={n}
            type="button"
            onClick={(e) => { e.stopPropagation(); onFlagClick && onFlagClick(n); }}
            title={`Changed — see Remark R${n}`}
            style={{background:'#fee2e2',color:'#b91c1c',border:'1px solid #fecaca',borderRadius:'4px',padding:'1px 5px',fontSize:'10px',fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:'2px'}}
          >⚑ R{n}</button>
        ))}
      </span>
    );
  };
  const [blList, setBlList] = useState(order.shipments || []);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(null); // 'new-bl'|'view-bl'|'edit-bl-info'|'edit-payment'|'edit-docs'|'edit-tracking'|'edit-logistics'|'edit-transport'
  const [form, setForm] = useState({});
  const [chaAgents, setChaAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shippingLines, setShippingLines] = useState([]);
  const [remarkModal, setRemarkModal] = useState(null); // { changes, note, save: ()=>{} }

  // ICD options for the Tracking dropdown come from the ICDs picked on the order,
  // not the full master list. Legacy singular icd_detail is included as a fallback,
  // and the BL's own saved icd_location is kept so old values still display.
  const orderIcdOptions = React.useMemo(() => {
    const out = [];
    if (Array.isArray(order.icd_details)) out.push(...order.icd_details);
    else if (order.icd_detail) out.push(order.icd_detail);
    if (form.icd_location && !out.includes(form.icd_location)) out.push(form.icd_location);
    return [...new Set(out.map(v => String(v).trim()).filter(Boolean))];
  }, [order.icd_details, order.icd_detail, form.icd_location]);

  const SHIPMENT_INTERNAL = new Set(['id','order_id','created_at','updated_at','created_by','updated_by','is_deleted','workflow_step','actual_amount_due','discount_amount']);
  const diffShipment = (oldSh, newForm) => {
    const out = [];
    for (const k of Object.keys(newForm)) {
      if (SHIPMENT_INTERNAL.has(k)) continue;
      const a = oldSh?.[k]; const b = newForm[k];
      const norm = v => v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
      if (norm(a) !== norm(b)) out.push({ scope: 'shipment', record_id: oldSh?.id, field: k, old_value: norm(a), new_value: norm(b) });
    }
    return out;
  };
  const guardedSave = async (saveFn) => {
    if ((order.workflow_step || 0) >= 2 && selected) {
      const changes = diffShipment(selected, form);
      if (changes.length > 0) {
        await saveFn();
        const lines = changes.map(c => `${SHIPMENT_FIELD_LABELS[c.field] || c.field}: ${c.old_value || '—'} → ${c.new_value || '—'}`).join('\n');
        const note = `BL ${selected.bl_no}\n${lines}`;
        try { await api.post(`/remarks/order/${order.id}`, { note, fields: changes }); }
        catch { /* non-fatal */ }
        return true; // we handled the save
      }
    }
    return false; // no changes, let caller handle
  };

  const loadShippingLines = async (shipmentId) => {
    try {
      const r = await api.get(`/payments/shipment/${shipmentId}/jv-lines`);
      setShippingLines(r.data.map(l => ({ head: l.head, head_custom: l.head_custom || '', amount: String(l.amount), gst_pct: String(l.gst_pct), bill_no: l.bill_no || '', bill_date: l.bill_date ? String(l.bill_date).split('T')[0] : '' })));
    } catch { setShippingLines([]); }
  };
  const addShippingLine = () => setShippingLines(ls => [...ls, { head: SHIPPING_HEADS[0], head_custom: '', amount: '', gst_pct: '18', bill_no: '', bill_date: '' }]);

  const loadUsdRate = async () => {
    try {
      const r = await api.get('/utils/usd-inr-rate');
      return r.data?.rate;
    } catch { return null; }
  };
  const updateShippingLine = (i, patch) => setShippingLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeShippingLine = (i) => setShippingLines(ls => ls.filter((_, idx) => idx !== i));
  const computeLineGst = (l) => r2((parseFloat(l.amount)||0) * (parseFloat(l.gst_pct)||0) / 100);
  const computeLineTotal = (l) => r2((parseFloat(l.amount)||0) + computeLineGst(l));

  const chaGstAmt = () => r2((parseFloat(form.cha_agency_charges)||0) * (parseFloat(form.cha_gst_pct)||0) / 100);
  // TDS = 2% of CHA Agency Charges (Basic); deducted from the Total.
  const chaTds    = () => r2((parseFloat(form.cha_agency_charges)||0) * 0.02);
  const chaTotal  = () => r2((parseFloat(form.cha_agency_charges)||0) + chaGstAmt() - chaTds());
  const trGstAmt  = () => r2((parseFloat(form.transport_charges)||0) * (parseFloat(form.transport_gst_pct)||0) / 100);
  const trTotal   = () => r2((parseFloat(form.transport_charges)||0) + trGstAmt());

  useEffect(() => {
    api.get('/masters/cha').then(r=>setChaAgents(r.data));
  }, []);
  useEffect(() => { setBlList(order.shipments || []); }, [order.shipments]);
  // Keep `selected` in sync after onRefresh — without this, the BL Info
  // view keeps showing pre-save values until the user re-clicks the row.
  useEffect(() => {
    if (!selected?.id) return;
    const fresh = (order.shipments || []).find(s => s.id === selected.id);
    if (fresh) setSelected(fresh);
  }, [order.shipments, selected?.id]);

  const openBL = (bl) => { setSelected(bl); setForm(normalizeDates(bl)); setMode('view-bl'); };
  const startNew = () => { setForm({ order_id: order.id, margin_days: 0, discount_amount: order.discount_amount ?? '' }); setMode('new-bl'); setSelected(null); };

  const saveNewBL = async e => {
    e.preventDefault();
    const mismatch = containerCountMismatch() || containerWeightMismatch();
    if (mismatch) { toast.error(mismatch); return; }
    setSaving(true);
    try {
      await api.post('/shipments', form);
      await saveOrderDiscount();
      toast.success('BL added'); onRefresh(); setMode(null);
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveTrackingStep = async () => {
    setSaving(true);
    try {
      // Valid Date stays hand-editable, but a corrupt / impossible year (e.g. the
      // "0002" a half-typed native date control can leave behind) is auto-healed
      // from the formula before saving. Plausible hand-picked dates are untouched.
      const payload = { ...form };
      if (Array.isArray(payload.container_dates)) {
        payload.container_dates = payload.container_dates.map(c => {
          const yr = parseInt(String(c.valid_date || '').slice(0, 4), 10);
          const bad = !c.valid_date || isNaN(yr) || yr < 1900 || yr > 2999;
          return bad
            ? { ...c, valid_date: calcValidDate(c.icd_arrival_date, form.margin_days) || '' }
            : c;
        });
      }
      await api.put(`/shipments/${selected.id}`, payload);
      toast.success('Tracking saved'); onRefresh(); setMode('view-bl');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const savePaymentStep = async () => {
    setSaving(true);
    try {
      const { lc_amount, payment_term, sblc_applied_date, sblc_approve_date, ...shipmentFields } = form;
      if (payment_term === 'SBLC' && (!sblc_applied_date || !sblc_approve_date)) {
        toast.error('SBLC Applied Date and SBLC Approve Date are required for SBLC');
        setSaving(false);
        return;
      }
      // LC Amount + Payment Term + SBLC dates live on the Order; persist them
      // there so they stay the single source of truth across all BLs of the order.
      await api.put(`/orders/${order.id}`, {
        lc_amount: lc_amount === '' ? null : lc_amount,
        payment_term: payment_term || null,
        sblc_applied_date: payment_term === 'SBLC' ? (sblc_applied_date || null) : null,
        sblc_approve_date: payment_term === 'SBLC' ? (sblc_approve_date || null) : null,
      });
      // The LC / payment figures edited here (payment type, LC date, due date,
      // exchange rate, USD value) live on the shipment itself, so they go
      // through the shipment update rather than the payments patch, whose
      // allow-list covers only the custom-duty side.
      await api.put(`/shipments/${selected.id}`, shipmentFields);
      await saveOrderDiscount();
      // The payments endpoint is what marks the BL as having reached step 5
      // (Payment), so it still gets the fields it owns even though the shipment
      // update above has already written them.
      await api.patch(`/payments/shipment/${selected.id}`, shipmentFields);
      // Cost sections B–F are edited here too, so they are written through the
      // same endpoints the JV Costs tab uses.
      await api.patch(`/payments/shipment/${selected.id}/jv-costs`, form);
      await api.put(`/payments/shipment/${selected.id}/jv-lines`, {
        lines: shippingLines.map(l => ({ head: l.head, head_custom: l.head_custom || null, amount: parseFloat(l.amount)||0, gst_pct: parseFloat(l.gst_pct)||0, bill_no: l.bill_no || null, bill_date: l.bill_date || null }))
      });
      toast.success('Saved'); onRefresh(); setMode('view-bl');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveBlInfoCore = async () => {
    setSaving(true);
    try {
      await api.put(`/shipments/${selected.id}`, form);
      await saveOrderDiscount();
      toast.success('BL Info updated'); onRefresh(); setMode('view-bl');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  const saveBlInfo = async () => {
    const mismatch = containerCountMismatch() || containerWeightMismatch();
    if (mismatch) { toast.error(mismatch); return; }
    if (await guardedSave(saveBlInfoCore)) return;
    saveBlInfoCore();
  };

  const savePatch = async (endpoint) => {
    setSaving(true);
    try {
      await api.patch(endpoint, form);
      toast.success('Saved'); onRefresh(); setMode('view-bl');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // Amount Due (USD) = this BL's Bill Weight × the order's Rate.
  const calcAmountDue = () => {
    const bw = parseFloat(form.bill_weight_mt || 0);
    const rate = parseFloat(order.rate_usd || 0);
    return bw && rate ? (bw * rate).toFixed(2) : '';
  };
  // Rate and Final Rate belong to the order and are read-only here; Discount is
  // editable and written back to the order, so Final Rate follows it live.
  const orderRateText = () => {
    const r = parseFloat(order.rate_usd);
    return isNaN(r) ? '' : r.toFixed(4);
  };
  const finalRateText = () => {
    const r = parseFloat(order.rate_usd);
    if (isNaN(r)) return '';
    return (r - (parseFloat(form.discount_amount) || 0)).toFixed(4);
  };
  // Exchange Rate and USD Value in INR are two views of the same figure:
  // value = Amount Due (USD) × rate. Typing either one fills in the other.
  const calcUsdValueInr = () => {
    const due = parseFloat(calcAmountDue());
    const ex = parseFloat(form.exchange_rate);
    return (!isNaN(due) && !isNaN(ex)) ? (due * ex).toFixed(2) : '';
  };
  // A BL saved before this field existed carries an exchange rate but no value,
  // so fill it in from the rate rather than waiting for someone to retype it.
  // Only when it is empty — an entered figure stays as typed.
  const usdValueInr = calcUsdValueInr();
  useEffect(() => {
    if (!usdValueInr) return;
    setForm(f => (f.usd_value == null || f.usd_value === '' ? { ...f, usd_value: usdValueInr } : f));
  }, [usdValueInr]);
  const exchangeRateField = () => (
    <div className="form-group">
      <label>Exchange Rate (INR/USD)</label>
      <input className="form-control" type="number" step="0.0001" value={form.exchange_rate ?? ''}
        onChange={e=>{
          const ex = e.target.value;
          const due = parseFloat(calcAmountDue());
          const rate = parseFloat(ex);
          setForm(f=>({
            ...f,
            exchange_rate: ex,
            usd_value: (!isNaN(due) && !isNaN(rate)) ? (due * rate).toFixed(2) : f.usd_value,
          }));
        }} />
    </div>
  );
  const rateFields = () => (
    <>
      <div className="form-group">
        <label>Order Rate (USD/MT)</label>
        <input className="form-control" readOnly value={orderRateText()} title="From the order" />
      </div>
      <div className="form-group">
        <label>Discount (USD)</label>
        <input className="form-control" type="number" step="0.01" value={form.discount_amount ?? ''}
          title="Saved on the order"
          onChange={e=>setForm(f=>({ ...f, discount_amount: e.target.value }))} />
      </div>
      <div className="form-group">
        <label>Final Rate (USD/MT)</label>
        <input className="form-control" readOnly value={finalRateText()} title="Order Rate − Discount" />
      </div>
    </>
  );
  const usdValueInrField = () => (
    <div className="form-group">
      <label>USD Value in INR</label>
      <input className="form-control" type="number" step="0.01" value={form.usd_value ?? ''}
        title="Amount Due in USD × Exchange Rate — editing this back-fills the rate"
        onChange={e=>{
          const val = e.target.value;
          const due = parseFloat(calcAmountDue());
          const inr = parseFloat(val);
          setForm(f=>({
            ...f,
            usd_value: val,
            exchange_rate: (!isNaN(due) && due !== 0 && !isNaN(inr)) ? (inr / due).toFixed(4) : f.exchange_rate,
          }));
        }} />
    </div>
  );
  // The order owns the Discount, so a change here is saved there.
  const saveOrderDiscount = async () => {
    const before = order.discount_amount == null ? '' : String(order.discount_amount);
    const now = form.discount_amount == null ? '' : String(form.discount_amount);
    if (before === now) return;
    const rate = parseFloat(order.rate_usd);
    await api.put(`/orders/${order.id}`, {
      discount_amount: now === '' ? null : now,
      ...(isNaN(rate) ? {} : { final_rate: (rate - (parseFloat(now) || 0)).toFixed(4) }),
    });
  };
  const calcDueDate = () => {
    if (!form.date_of_bl || form.margin_days == null) return '';
    const d = new Date(form.date_of_bl);
    d.setDate(d.getDate() + parseInt(form.margin_days || 0));
    return d.toISOString().split('T')[0];
  };

  // The BLs of an order can never hold more containers than the order itself,
  // so what is left for this BL is the order's count minus every other BL's.
  const containerBudget = (() => {
    const total = parseInt(order.no_of_containers);
    if (isNaN(total)) return null;
    const usedElsewhere = blList
      .filter(b => !selected || b.id !== selected.id)
      .reduce((sum, b) => sum + (parseInt(b.no_of_containers) || 0), 0);
    return { total, usedElsewhere, left: Math.max(0, total - usedElsewhere) };
  })();

  // Under DP only the Payment Date can be entered — Date LC Made and Due Date
  // stay on screen but disabled. LC / SBLC let the user set all three.
  const isDP = form.payment_type === 'DP';
  const paymentTypeField = () => (
    <div className="form-group">
      <label>Payment Type</label>
      <select className="form-control" value={form.payment_type || ''}
        onChange={e=>{
          const v = e.target.value;
          setForm(f => ({
            ...f,
            payment_type: v,
            ...(v === 'DP' ? { date_lc_made: '', due_date: '' } : {}),
          }));
        }}>
        <option value="">— Select —</option>
        {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
    </div>
  );
  const dateLcMadeField = () => (
    <div className="form-group">
      <label>Date LC Made</label>
      <DateInput className="form-control" value={form.date_lc_made || ''} disabled={isDP}
        title={isDP ? 'Only for LC / SBLC' : undefined}
        onChange={e=>setForm(f=>({ ...f, date_lc_made: e.target.value }))} />
    </div>
  );
  const dueDateField = () => (
    <div className="form-group">
      <label>Due Date</label>
      <DateInput className="form-control" value={form.due_date || calcDueDate() || ''} disabled={isDP}
        title={isDP ? 'DP — Due Date is BL Date + Free days' : 'Auto = BL Date + Free days — editable'}
        onChange={e=>setForm(f=>({ ...f, due_date: e.target.value }))} />
    </div>
  );
  // Shared by the new-BL form and the Payment step, which is where an existing
  // BL's LC / payment figures are edited.
  const lcPaymentDetailsSection = (title = 'LC / Payment Details') => (
    <>
      <div className="section-title mt-2">{title}</div>
      <div className="form-row-4">
        {paymentTypeField()}
        <div className="form-group">
          <label>Payment Date</label>
          <DateInput className="form-control" value={form.payment_date||''} onChange={e=>{
            const pd = e.target.value;
            setForm(f=>({...f, payment_date: pd, payment_status: pd ? 'Paid' : 'Pending'}));
          }} />
        </div>
        {dateLcMadeField()}
        <div className="form-group" />
      </div>
      <div className="form-row-4">
        {rateFields()}
        <div className="form-group"><label>Amount Due in USD</label><input className="form-control" readOnly value={calcAmountDue()} title="Bill Weight × Order Rate" /></div>
      </div>
      <div className="form-row-4">
        {dueDateField()}
        {exchangeRateField()}
        {usdValueInrField()}
        <div className="form-group" />
      </div>
    </>
  );
  // Cost sections B–F of the Journal Voucher. Edited in the Payment step;
  // Payment Summary (A) stays on the JV Costs tab.
  const jvCostSectionsBtoF = () => (
    <>
              <div className="section-title mt-2" style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <span>B. Shipping Line & Other</span>
                <button type="button" className="btn btn-outline btn-xs" onClick={addShippingLine}>+ Add Line</button>
              </div>
              <div className="table-wrap" style={{marginBottom:'8px'}}>
                <table>
                  <thead><tr><th style={{width:'18%'}}>Head</th><th style={{width:'12%'}}>Bill No</th><th style={{width:'12%'}}>Bill Date</th><th style={{width:'12%'}}>Amount (INR)</th><th style={{width:'8%'}}>GST %</th><th style={{width:'12%'}}>GST Amount</th><th style={{width:'12%'}}>Total</th><th></th></tr></thead>
                  <tbody>
                    {shippingLines.length === 0 && (
                      <tr><td colSpan={8} style={{textAlign:'center',color:'var(--text-muted)',padding:'10px'}}>No line items — click "+ Add Line" below.</td></tr>
                    )}
                    {shippingLines.map((l, i) => (
                      <tr key={i}>
                        <td>
                          <select className="form-control" value={l.head} onChange={e=>updateShippingLine(i,{head:e.target.value, head_custom: e.target.value==='Others' ? (l.head_custom||'') : ''})}>
                            {SHIPPING_HEADS.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                          {l.head === 'Others' && (
                            <input className="form-control mt-1" placeholder="Enter head name" style={{marginTop:'4px'}} value={l.head_custom||''} onChange={e=>updateShippingLine(i,{head_custom:e.target.value})} />
                          )}
                        </td>
                        <td><input className="form-control" value={l.bill_no||''} onChange={e=>updateShippingLine(i,{bill_no:e.target.value})} /></td>
                        <td><DateInput className="form-control" value={l.bill_date||''} onChange={e=>updateShippingLine(i,{bill_date:e.target.value})} /></td>
                        <td><input className="form-control" type="number" step="0.01" value={l.amount} onChange={e=>updateShippingLine(i,{amount:e.target.value})} /></td>
                        <td><input className="form-control" type="number" step="0.01" value={l.gst_pct} onChange={e=>updateShippingLine(i,{gst_pct:e.target.value})} /></td>
                        <td><input className="form-control" readOnly value={computeLineGst(l).toFixed(2)} /></td>
                        <td><input className="form-control" readOnly value={computeLineTotal(l).toFixed(2)} /></td>
                        <td><button type="button" className="btn btn-outline btn-xs" onClick={()=>removeShippingLine(i)}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="section-title mt-2">C. Custom Duty</div>
              <div className="form-row-3">
                {inp('Bill of Entry No','bill_of_entry_no',form,setForm)}
                {inp('Bill of Entry Date','bill_of_entry_date',form,setForm,'date')}
                {inp('Custom Duty','estimated_custom_duty',form,setForm,'number')}
                {inp('IGST on Custom Duty','igst_on_custom_duty',form,setForm,'number')}
                {inp('Custom Duty Interest','custom_duty_interest',form,setForm,'number')}
                {inp('Penalty / Other Charges','custom_duty_penalty',form,setForm,'number')}
              </div>
              <div className="section-title mt-2">D. CHA / Agency</div>
              <div className="form-row-3">
                {inp('Bill No','cha_bill_no',form,setForm)}
                {inp('Bill Date','cha_bill_date',form,setForm,'date')}
                {inp('CHA Agency Charges (Basic)','cha_agency_charges',form,setForm,'number')}
              </div>
              <div className="form-row-4">
                <div className="form-group"><label>TDS (2%)</label><input className="form-control" readOnly value={chaTds().toFixed(2)} /></div>
                {inp('GST %','cha_gst_pct',form,setForm,'number')}
                <div className="form-group"><label>GST Amount</label><input className="form-control" readOnly value={chaGstAmt().toFixed(2)} /></div>
                <div className="form-group"><label>Total</label><input className="form-control" readOnly value={chaTotal().toFixed(2)} /></div>
              </div>
              <div className="section-title mt-2">E. Transportation</div>
              <div className="form-row-3">
                {inp('Builty No','transport_builty_no',form,setForm)}
                {inp('Builty Date','transport_builty_date',form,setForm,'date')}
                {inp('Transport Charges (Basic)','transport_charges',form,setForm,'number')}
              </div>
              <div className="form-row-3">
                {inp('GST %','transport_gst_pct',form,setForm,'number')}
                <div className="form-group"><label>GST Amount</label><input className="form-control" readOnly value={trGstAmt().toFixed(2)} /></div>
                <div className="form-group"><label>Total</label><input className="form-control" readOnly value={trTotal().toFixed(2)} /></div>
              </div>
              <div className="section-title mt-2">F. Bank & Misc</div>
              <div className="form-row-3">
                {inp('Bank Charges','bank_charges',form,setForm,'number')}
                {inp('Misc Charges','misc_charges',form,setForm,'number')}
              </div>
    </>
  );

  // One container row per container. Neither side rewrites the other while the
  // form is open; the two are reconciled when the BL is saved.
  const blankContainer = () => ({ container_no: '', weight: '', rail_out_date: '', icd_arrival_date: '' });
  const containerCountMismatch = () => {
    const rows = Array.isArray(form.container_dates) ? form.container_dates.length : 0;
    const cnt = parseInt(form.no_of_containers);
    // No container rows listed yet (e.g. while creating a new BL, where the
    // container list is filled in later via Edit BL Info) — nothing to check.
    if (rows === 0) return null;
    if (isNaN(cnt) || rows === cnt) return null;
    return `No. of Containers is ${cnt} but ${rows} container${rows === 1 ? '' : 's'} listed — they must match`;
  };
  // Container weights must add up to the Bill Weight. Rows left blank are a BL
  // whose weights are not known yet, so nothing is checked until at least one
  // is filled in; from then on the total has to land exactly on Bill Weight.
  const containerWeightTotal = () => {
    const arr = Array.isArray(form.container_dates) ? form.container_dates : [];
    return arr.reduce((s, r) => s + (parseFloat(r.weight) || 0), 0);
  };
  const containerWeightMismatch = () => {
    const arr = Array.isArray(form.container_dates) ? form.container_dates : [];
    if (!arr.some(r => r.weight !== '' && r.weight != null)) return null;
    const bw = parseFloat(form.bill_weight_mt);
    if (isNaN(bw)) return null;
    const total = containerWeightTotal();
    if (Math.abs(total - bw) < 0.0005) return null;
    const diff = total - bw;
    return `Container weights total ${total.toFixed(3)} MT but Bill Weight is ${bw.toFixed(3)} MT — ${Math.abs(diff).toFixed(3)} MT ${diff > 0 ? 'over' : 'short'}`;
  };

  // No. of Containers — capped at the order's remaining containers, and also
  // re-derives Bill Weight, which the user may then overwrite by hand.
  const containersField = () => {
    const entered = parseInt(form.no_of_containers);
    const rows = Array.isArray(form.container_dates) ? form.container_dates.length : 0;
    const over = containerBudget && !isNaN(entered) && entered > containerBudget.left;
    const under = !isNaN(entered) && entered < rows;
    const tip = [
      containerBudget && `Order has ${containerBudget.total} containers, ${containerBudget.usedElsewhere} already on other BLs — at most ${containerBudget.left} here`,
      rows > 0 && `${rows} container${rows === 1 ? '' : 's'} listed below, so it cannot go under ${rows}`,
    ].filter(Boolean).join('. ') || undefined;
    return (
      <div className="form-group">
        <label>No. of Containers
          {(over || under) && (
            <span title={tip}
              style={{ display:'inline-flex', alignItems:'center', gap:'2px',
                background:'#dc2626', color:'#fff', fontSize:'10px', fontWeight:700,
                borderRadius:'10px', padding:'1px 7px', marginLeft:'6px', cursor:'help' }}>
              ⚠ {over ? `max ${containerBudget.left}` : `min ${rows}`}
            </span>
          )}
        </label>
        <input className="form-control" type="number" step="1" min={rows || 0}
          max={containerBudget ? containerBudget.left : undefined}
          title={tip}
          value={form.no_of_containers || ''}
          style={(over || under) ? { borderColor:'#dc2626' } : undefined}
          onChange={e=>{
            let cnt = e.target.value;
            // Hard caps — neither past the order's remaining containers, nor
            // below the container rows already listed on this BL.
            if (containerBudget && cnt !== '' && parseInt(cnt) > containerBudget.left) {
              cnt = String(containerBudget.left);
            }
            if (cnt !== '' && rows > 0 && parseInt(cnt) < rows) {
              cnt = String(rows);
            }
            const avg = parseFloat(order.avg_loading);
            const bw = (cnt !== '' && !isNaN(parseFloat(cnt)) && !isNaN(avg))
              ? (parseFloat(cnt) * avg).toFixed(3)
              : '';
            setForm(f=>({ ...f, no_of_containers: cnt, bill_weight_mt: bw, avg_loading: avgLoadingOf(bw, cnt) }));
          }} />
      </div>
    );
  };

  // Bill Weight — auto-filled from No. of Containers × the order's Avg Loading,
  // but editable: a typed value stands until the container count changes again.
  const billWeightField = () => (
    <div className="form-group">
      <label>Bill Weight (MT)</label>
      <input className="form-control" type="number" step="0.001" value={form.bill_weight_mt || ''}
        title={`Auto = No. of Containers × order's Avg Loading (${order.avg_loading ?? '—'}) — editable`}
        onChange={e=>{
          const bw = e.target.value;
          setForm(f=>({ ...f, bill_weight_mt: bw, avg_loading: avgLoadingOf(bw, f.no_of_containers) }));
        }} />
    </div>
  );

  // Avg Loading on a BL is read-only — always Bill Weight ÷ No. of Containers.
  const avgLoadingOf = (bw, cnt) => {
    const b = parseFloat(bw), c = parseFloat(cnt);
    return (!isNaN(b) && !isNaN(c) && c !== 0) ? (b / c).toFixed(3) : '';
  };
  const avgLoadingField = () => (
    <div className="form-group">
      <label>Avg Loading (MT/cont)</label>
      <input className="form-control" readOnly
        value={avgLoadingOf(form.bill_weight_mt, form.no_of_containers)}
        title="Auto = Bill Weight ÷ No. of Containers" />
    </div>
  );

  const statusBadge = s => {
    const m = { 'Under Sea':'badge-blue','On Ship':'badge-blue','At Port':'badge-orange','Customs':'badge-yellow','Released':'badge-green','Pending':'badge-red','Partial':'badge-yellow','Paid':'badge-green' };
    return <span className={`badge ${m[s]||'badge-gray'}`}>{s}</span>;
  };

  return (
    <div>
      <div className="page-header" style={{marginBottom:'12px'}}>
        <div><h3 style={{fontSize:'16px',fontWeight:700}}>BL / Shipments ({blList.length})</h3></div>
        <button className="btn btn-primary btn-sm" onClick={startNew}>+ Add BL</button>
      </div>

      {/* BL List */}
      {blList.length === 0 && mode !== 'new-bl' && <div className="empty-state">No BLs yet. Click "+ Add BL" to add shipment details.</div>}

      {blList.length > 0 && (
        <div className="card" style={{padding:0,marginBottom:'16px'}}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>BL No</th><th>BL Date</th><th>Lot No</th><th>Bill Wt</th><th>Containers</th><th>Port Status</th><th>Payment</th><th>Due Date</th><th>Actions</th></tr></thead>
              <tbody>
                {blList.map(bl => (
                  <tr key={bl.id} style={{cursor:'pointer'}} onClick={() => openBL(bl)}>
                    <td className="fw-bold text-primary">{bl.bl_no}</td>
                    <td>{fmtDate(bl.date_of_bl)}</td>
                    <td>{bl.lot_no||'—'}</td>
                    <td>{bl.bill_weight_mt ? `${bl.bill_weight_mt} MT` : '—'}</td>
                    <td>{bl.no_of_containers||'—'}</td>
                    <td>{bl.port_status ? statusBadge(bl.port_status) : '—'}</td>
                    <td>{bl.payment_status ? statusBadge(bl.payment_status) : '—'}</td>
                    <td style={{color: bl.due_date && new Date(bl.due_date)<new Date() && bl.payment_status!=='Paid' ? 'var(--accent)' : 'inherit'}}>{fmtDate(bl.due_date)}</td>
                    <td onClick={e=>e.stopPropagation()}>
                      <button className="btn btn-outline btn-xs" onClick={()=>openBL(bl)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New BL Form */}
      {mode === 'new-bl' && (
        <div className="card">
          <div className="section-title">Add BL / Shipment (Step 3)</div>
          <form onSubmit={saveNewBL}>
            <div className="form-row-4">
              {inp('BL Number *','bl_no',form,setForm,'text',true)}
              {inp('Date of BL','date_of_bl',form,setForm,'date')}
              {inp('Lot Number','lot_no',form,setForm)}
              {containersField()}
            </div>
            <div className="form-row-4">
              {billWeightField()}
              {avgLoadingField()}
              {inp('Port of Loading','port_of_loading',form,setForm)}
              {inp('Port of Discharge','port_of_discharge',form,setForm)}
            </div>
            <div className="form-row-4">
              <div className="form-group">
                <label>Shipping Line</label>
                <ShippingLinesPicker
                  options={Array.isArray(order.shipping_lines) ? order.shipping_lines.filter(Boolean) : []}
                  value={Array.isArray(form.shipping_lines) ? form.shipping_lines : []}
                  onChange={(arr)=>setForm(f=>({...f, shipping_lines: arr, shipping_line: arr[0] || ''}))}
                />
              </div>
              {inp('Free days (if any)','margin_days',form,setForm,'number')}
              <div className="form-group" />
              <div className="form-group" />
            </div>
            {lcPaymentDetailsSection()}
            <div className="btn-group mt-2">
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving...':'Save BL'}</button>
              <button className="btn btn-secondary" type="button" onClick={()=>setMode(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* BL Detail View */}
      {selected && ['view-bl','edit-bl-info','edit-tracking','edit-payment','receipt'].includes(mode) && (
        <div className="card">
          <div className="modal-header">
            <h3>BL: {selected.bl_no}</h3>
            <button className="btn btn-secondary btn-sm" onClick={()=>setMode(null)}>Close</button>
          </div>
          <div className="tabs" style={{marginTop:'0'}}>
            {[['view-bl','BL Info'],['edit-tracking','Tracking'],['edit-payment','Payment'],['receipt','Material Receipt']].map(([k,l])=>(
              <div key={k} className={`tab${mode===k?' active':''}`} onClick={async ()=>{
                const base = normalizeDates(selected);
                if (k === 'edit-tracking') {
                  // Auto-fill Valid Date = ICD Arrival + Margin Days − 1
                  // when ICD Arrival is set (override only if blank).
                  const auto = calcValidDate(base.icd_arrival_date, base.margin_days);
                  if (auto && !base.free_time_upto) base.free_time_upto = auto;
                }
                if (k === 'edit-payment') {
                  // Seed order-level fields so user can edit them in this tab too.
                  // Discount is one of them — it is saved on the order, so
                  // without this the field looks empty on every re-open.
                  base.discount_amount = order.discount_amount ?? '';
                  base.lc_amount = order.lc_amount || '';
                  base.payment_term = order.payment_term || '';
                  base.sblc_applied_date = order.sblc_applied_date ? String(order.sblc_applied_date).split('T')[0] : '';
                  base.sblc_approve_date = order.sblc_approve_date ? String(order.sblc_approve_date).split('T')[0] : '';
                  // Amount in Dollar = Bill Weight (MT) × order's Final Rate (USD/MT).
                  // Always recomputed on tab open so it stays in sync with both inputs.
                  {
                    const bw = parseFloat(base.bill_weight_mt) || 0;
                    const rate = parseFloat(order.final_rate) || 0;
                    base.amount_usd = bw && rate ? r2(bw * rate).toFixed(2) : '';
                  }
                  // Final Amount = Amount in Dollar − Discount. Always
                  // recomputed on tab open (same as amount_usd) so it never
                  // shows a stale stored value.
                  {
                    const disc = parseFloat(base.discount_usd) || 0;
                    base.final_amount_usd = base.amount_usd
                      ? r2(parseFloat(base.amount_usd) - disc).toFixed(2)
                      : '';
                  }
                  // Re-derive INR off the freshly computed final amount.
                  if (base.final_amount_usd && base.rate_per_usd) {
                    base.amount_inr = r2(parseFloat(base.final_amount_usd) * parseFloat(base.rate_per_usd)).toFixed(2);
                  }
                }
                if (k === 'edit-payment') {
                  if (base.cha_gst_pct == null || base.cha_gst_pct === '') base.cha_gst_pct = 18;
                  if (base.transport_gst_pct == null || base.transport_gst_pct === '') base.transport_gst_pct = 18;
                  if (!base.buyers_credit_rate) {
                    const live = await loadUsdRate();
                    if (live) base.buyers_credit_rate = r2(live);
                  }
                }
                setForm(base); setMode(k);
                if (k === 'edit-payment') loadShippingLines(selected.id);
              }}>{l}</div>
            ))}
          </div>

          {mode === 'view-bl' && (
            <>
              <div style={{display:'flex',justifyContent:'flex-end',marginTop:'8px'}}>
                <button className="btn btn-outline btn-sm" onClick={()=>{
                  const base = normalizeDates(selected);
                  base.discount_amount = order.discount_amount ?? '';
                  // Seed avg_loading from bill_weight ÷ containers if blank,
                  // so the field doesn't appear empty for existing BLs.
                  if (!base.avg_loading) {
                    const bw = parseFloat(base.bill_weight_mt) || 0;
                    const cnt = parseFloat(base.no_of_containers) || 0;
                    if (bw && cnt) base.avg_loading = r2(bw/cnt).toFixed(3);
                  }
                  setForm(base);
                  setMode('edit-bl-info');
                }}>Edit BL Info</button>
              </div>
              <div className="grid-2" style={{marginTop:'12px'}}>
                <div>
                  {(() => {
                    const bw = parseFloat(selected.bill_weight_mt) || 0;
                    const cnt = parseFloat(selected.no_of_containers) || 0;
                    const computedAvg = bw && cnt ? r2(bw/cnt).toFixed(3) : '';
                    const avgDisplay = selected.avg_loading || computedAvg;
                    return [['BL No',selected.bl_no,'bl_no'],['BL Date',fmtDate(selected.date_of_bl),'date_of_bl'],['Lot No',selected.lot_no||'—','lot_no'],['Bill Weight',selected.bill_weight_mt?`${selected.bill_weight_mt} MT`:'—','bill_weight_mt'],['Containers',selected.no_of_containers||'—','no_of_containers'],['Avg Loading',avgDisplay?`${avgDisplay} MT/cont`:'—','avg_loading'],['Port of Loading',selected.port_of_loading||'—','port_of_loading'],['Port of Discharge',selected.port_of_discharge||'—','port_of_discharge']];
                  })().map(([l,v,k])=>(
                    <div key={l} style={{display:'flex',marginBottom:'6px',gap:'12px',alignItems:'center'}}>
                      <span style={{minWidth:'130px',color:'var(--text-muted)',fontSize:'12px',fontWeight:600}}>{l}</span><span>{v}</span>
                      <FlagBadge scope="shipment" id={selected.id} field={k} />
                    </div>
                  ))}
                </div>
                <div>
                  {[['Payment Type',selected.payment_type||'—','payment_type'],['Margin Days',selected.margin_days||'0','margin_days'],['Port Status',selected.port_status||'—','port_status']].map(([l,v,k])=>(
                    <div key={l} style={{display:'flex',marginBottom:'6px',gap:'12px',alignItems:'center'}}>
                      <span style={{minWidth:'130px',color:'var(--text-muted)',fontSize:'12px',fontWeight:600}}>{l}</span><span>{v}</span>
                      <FlagBadge scope="shipment" id={selected.id} field={k} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Container numbers list (dates edited in Tracking step) */}
              {(() => {
                const arr = Array.isArray(selected.container_dates) ? selected.container_dates : [];
                if (arr.length === 0) return null;
                const totalWt = arr.reduce((t, r) => t + (parseFloat(r.weight) || 0), 0);
                return (
                  <div style={{marginTop:'16px'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'12px'}}>
                      <div className="section-title" style={{margin:0}}>Containers ({arr.length})</div>
                      <div style={{fontSize:'13px',fontWeight:600}}>Total: <span style={{color:'var(--primary,#1d4ed8)'}}>{totalWt.toFixed(3)} MT</span></div>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th style={{width:'30px'}}>#</th><th style={{width:'220px'}}>Container No</th><th style={{width:'140px'}}>Weight (MT)</th><th></th></tr></thead>
                        <tbody>
                          {arr.map((r, i) => (
                            <tr key={i}>
                              <td style={{color:'var(--text-muted)'}}>{i + 1}</td>
                              <td className="fw-bold">{r.container_no || '—'}</td>
                              <td>{r.weight ? Number(r.weight).toFixed(3) : '—'}</td>
                              <td></td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{textAlign:'right',fontWeight:700}}>Total</td>
                            <td style={{fontWeight:700}}>{totalWt.toFixed(3)} MT</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {mode === 'edit-bl-info' && (
            <form onSubmit={e=>{e.preventDefault();saveBlInfo();}} style={{marginTop:'12px'}}>
              <div className="alert alert-info">BL Number is fixed once a shipment is created — other fields can be edited here.</div>
              <div className="form-row-4">
                <div className="form-group"><label>BL Number</label><input className="form-control" value={selected.bl_no||''} readOnly /></div>
                {inp('Date of BL','date_of_bl',form,setForm,'date')}
                {inp('Lot Number','lot_no',form,setForm)}
                {containersField()}
              </div>
              <div className="form-row-4">
                {billWeightField()}
                {avgLoadingField()}
                {inp('Port of Loading','port_of_loading',form,setForm)}
                {inp('Port of Discharge','port_of_discharge',form,setForm)}
              </div>
              <div className="form-row-4">
                <div className="form-group">
                  <label>Shipping Line</label>
                  <ShippingLinesPicker
                    options={Array.isArray(order.shipping_lines) ? order.shipping_lines.filter(Boolean) : []}
                    value={Array.isArray(form.shipping_lines) ? form.shipping_lines : []}
                    onChange={(arr)=>setForm(f=>({...f, shipping_lines: arr, shipping_line: arr[0] || ''}))}
                  />
                </div>
                {inp('Free days (if any)','margin_days',form,setForm,'number')}
                <div className="form-group" />
                <div className="form-group" />
              </div>
              {/* Containers list — dynamic add/remove. Only container_no
                  is captured here; rail-out and ICD arrival dates are
                  edited per container in the Tracking step. */}
              {(() => {
                const arr = Array.isArray(form.container_dates) ? form.container_dates : [];
                // The list never rewrites No. of Containers — the save checks
                // that the two agree.
                const update = (next) => setForm(f => ({ ...f, container_dates: next }));
                const updateField = (i, patch) => {
                  const next = arr.slice();
                  next[i] = { ...next[i], ...patch };
                  update(next);
                };
                const addRow = () => {
                  if (containerBudget && arr.length + 1 > containerBudget.left) {
                    toast.error(`Order has ${containerBudget.total} containers, ${containerBudget.usedElsewhere} already on other BLs — only ${containerBudget.left} can be listed on this BL`);
                    return;
                  }
                  update([...arr, blankContainer()]);
                };
                const removeRow = (i) => update(arr.filter((_, idx) => idx !== i));
                const totalWt = arr.reduce((s, r) => s + (parseFloat(r.weight) || 0), 0);
                return (
                  <>
                    <div style={{display:'flex', alignItems:'center', gap:'12px', marginTop:'12px', marginBottom:'8px'}}>
                      <div className="section-title" style={{margin:0}}>Containers ({arr.length})</div>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>+ Add Container</button>
                      {totalWt > 0 && (() => {
                        const wtError = containerWeightMismatch();
                        return (
                          <span style={{fontSize:'12px', color: wtError ? 'var(--danger, #d32f2f)' : 'var(--text-muted)'}}
                            title={wtError || 'Matches Bill Weight'}>
                            Total: {totalWt.toFixed(3)} MT
                            {wtError && ` — must equal Bill Weight ${parseFloat(form.bill_weight_mt).toFixed(3)} MT`}
                          </span>
                        );
                      })()}
                    </div>
                    {arr.length === 0 && (
                      <div className="text-muted" style={{margin:'4px 0 8px', fontSize:'13px'}}>No containers added yet.</div>
                    )}
                    {arr.map((row, i) => (
                      <div key={i} style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', maxWidth:'620px'}}>
                        <span style={{fontSize:'12px', color:'var(--text-muted)', minWidth:'22px'}}>{i + 1}.</span>
                        <input
                          className="form-control"
                          style={{flex:1, padding:'5px 8px', fontSize:'12.5px'}}
                          placeholder="Container No (e.g. ABCD1234567)"
                          value={row.container_no || ''}
                          onChange={e=>updateField(i, { container_no: e.target.value })}
                        />
                        <input
                          className="form-control"
                          style={{width:'140px', padding:'5px 8px', fontSize:'12.5px'}}
                          type="number" step="0.001"
                          placeholder="Weight (MT)"
                          value={row.weight || ''}
                          onChange={e=>updateField(i, { weight: e.target.value })}
                        />
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          style={{padding:'4px 8px'}}
                          onClick={()=>removeRow(i)}
                          title="Remove"
                        >×</button>
                      </div>
                    ))}
                  </>
                );
              })()}

              <div className="btn-group mt-2">
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving...':'Save BL Info'}</button>
                <button className="btn btn-secondary" type="button" onClick={()=>setMode('view-bl')}>Cancel</button>
              </div>
            </form>
          )}

          {mode === 'edit-tracking' && (
            <form onSubmit={e=>{e.preventDefault();saveTrackingStep();}} style={{marginTop:'12px'}}>
              <div className="form-row-4">
                <div className="form-group"><label>CHA Agent</label><select className="form-control" value={form.cha_id||''} onChange={e=>{const c=chaAgents.find(x=>x.id===parseInt(e.target.value));setForm(f=>({...f,cha_id:e.target.value,cha_name:c?.name||''}))}}>
                  <option value="">—</option>{chaAgents.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                {inp('Date Mail to CHA','date_mail_to_cha',form,setForm,'date')}
                {inp('ETA at Port','eta_date_port',form,setForm,'date')}
                {inp('Actual Arrival at Port','arrival_date_port',form,setForm,'date')}
              </div>
              <div className="form-row-4">
                <div className="form-group">
                  <label>ICD Location</label>
                  <select className="form-control" value={form.icd_location||''} onChange={e=>setForm(f=>({...f,icd_location:e.target.value}))}>
                    <option value="">— Select ICD —</option>
                    {orderIcdOptions.map(name=><option key={name}>{name}</option>)}
                  </select>
                </div>
                {inp('Doc Release Date (Bank)','doc_release_date',form,setForm,'date')}
                {inp('Courier Date to CHA','courier_date_to_cha',form,setForm,'date')}
              </div>

              {/* Per-container tracking dates — container_no read-only,
                  Rail Out / ICD Arrival / Valid Date editable per container. */}
              {(() => {
                const arr = Array.isArray(form.container_dates) ? form.container_dates : [];
                if (arr.length === 0) {
                  return (
                    <div className="alert alert-info mt-2">No containers added yet. Add containers from BL Info first.</div>
                  );
                }
                const updateRow = (i, patch) => {
                  const next = arr.slice();
                  next[i] = { ...next[i], ...patch };
                  setForm(f => ({ ...f, container_dates: next }));
                };
                return (
                  <>
                    <div className="section-title mt-2">Container Tracking ({arr.length})</div>
                    {arr.map((row, i) => (
                      <div key={i} style={{display:'grid',gridTemplateColumns:'repeat(5, minmax(0, 1fr))',gap:'8px',marginBottom:'8px',width:'100%'}}>
                        <div className="form-group" style={{minWidth:0}}>
                          <label>Container {i + 1} No.</label>
                          <input className="form-control" style={{width:'100%',minWidth:0,boxSizing:'border-box'}} value={row.container_no || ''} readOnly />
                        </div>
                        <div className="form-group" style={{minWidth:0}}>
                          <label>Weight (MT)</label>
                          <input className="form-control" readOnly style={{width:'100%',minWidth:0,boxSizing:'border-box'}} type="number" step="0.001" value={row.weight || ''} />
                        </div>
                        <div className="form-group" style={{minWidth:0}}>
                          <label>Rail Out Date</label>
                          <DateInput className="form-control" style={{width:'100%',minWidth:0,boxSizing:'border-box'}} value={row.rail_out_date || ''} onChange={e=>updateRow(i,{rail_out_date:e.target.value})} />
                        </div>
                        <div className="form-group" style={{minWidth:0}}>
                          <label>ICD Arrival Date</label>
                          <DateInput className="form-control" style={{width:'100%',minWidth:0,boxSizing:'border-box'}} value={row.icd_arrival_date || ''} onChange={e=>{
                            const icd = e.target.value;
                            const auto = calcValidDate(icd, form.margin_days);
                            // Auto-fill Valid Date only when it is empty — a hand-picked
                            // value is preserved. (Save also heals corrupt years.)
                            updateRow(i, { icd_arrival_date: icd, valid_date: row.valid_date || auto || '' });
                          }} />
                        </div>
                        <div className="form-group" style={{minWidth:0}}>
                          <label>Valid Date</label>
                          <DateInput className="form-control" style={{width:'100%',minWidth:0,boxSizing:'border-box'}} value={row.valid_date || ''} onChange={e=>updateRow(i,{valid_date:e.target.value})} />
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}

              <div className="btn-group mt-2"><button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving...':'Save Tracking'}</button><button className="btn btn-secondary" type="button" onClick={()=>setMode('view-bl')}>Cancel</button></div>
            </form>
          )}

          {mode === 'edit-payment' && (
            <form onSubmit={e=>{e.preventDefault();savePaymentStep();}} style={{marginTop:'12px'}}>
              {lcPaymentDetailsSection('A. LC / Payment Details')}
              {jvCostSectionsBtoF()}
              <div className="btn-group mt-2"><button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving...':'Save Payment'}</button><button className="btn btn-secondary" type="button" onClick={()=>setMode('view-bl')}>Cancel</button></div>
            </form>
          )}

          {/* Material Receipt is kept per order — one receipt covers every BL —
              so the same panel is shown here, after Tracking. */}
          {mode === 'receipt' && <ReceiptPanel order={order} onRefresh={onRefresh} />}

        </div>
      )}

      {remarkModal && (
        <div onClick={()=>!saving && setRemarkModal(null)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'12px',padding:'24px',width:'min(560px,94vw)',maxHeight:'85vh',overflow:'auto',boxShadow:'0 18px 48px rgba(15,23,42,0.25)'}}>
            <h3 style={{margin:'0 0 6px',fontSize:'17px',fontWeight:700}}>Remark for these BL changes</h3>
            <p style={{color:'var(--text-muted)',fontSize:'12px',marginTop:0}}>Order is past the Indent stage. Briefly note <i>why</i> these BL fields are changing — flagged in the print PDF.</p>
            <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:'8px',padding:'10px',fontSize:'12px',marginBottom:'12px'}}>
              {remarkModal.changes.map((c,i) => (
                <div key={i} style={{padding:'2px 0'}}><b>{c.field}</b>: <span style={{color:'#b91c1c'}}>{c.old_value || '—'}</span> → <span style={{color:'#15803d'}}>{c.new_value || '—'}</span></div>
              ))}
            </div>
            <label style={{fontSize:'12px',fontWeight:600,color:'var(--text-muted)'}}>Remark <span style={{color:'var(--accent)'}}>*</span></label>
            <textarea autoFocus className="form-control" rows={3} value={remarkModal.note} onChange={e=>setRemarkModal({...remarkModal, note: e.target.value})} placeholder="e.g. Containers reduced by shipper; weight revised after re-weighment" />
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',marginTop:'14px'}}>
              <button className="btn btn-secondary btn-sm" onClick={()=>setRemarkModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={async ()=>{
                if (!remarkModal.note.trim()) { toast.error('Remark is required'); return; }
                const note = remarkModal.note.trim();
                await remarkModal.run(note);
                setRemarkModal(null);
              }} disabled={saving || !remarkModal.note.trim()}>{saving?'Saving…':'Save with Remark'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
